import { Component, ChangeDetectionStrategy, TemplateRef, ChangeDetectorRef, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { catchError, take, tap } from 'rxjs/operators';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EMPTY, forkJoin, of } from 'rxjs';
import { IrisLanguageI } from '@iris/common/models/IrisLanguage';
import merge from 'lodash/merge';
import { IrisGlobalSettingsI } from '@iris/common/models/IrisGlobalSettings';
import { IrisModuleAliasType, IrisGlobalSettingsService } from '@iris/common/services/settings/global-settings.service';
import { IrisDeeplTranslationService } from '@iris/common/services/deepl-translation.service';
import { IrisDeeplResultI } from '@iris/common/models/IrisDeeplLanguage';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { IrisSimpleLoaderModule } from '@iris/common/modules/iris-simple-loader/simple-loader.module';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';
import {
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective
} from '@ngx-strakit/cdk';

export interface TranslationsModalDataI {
  controlTemplate: TemplateRef<unknown>;
  translations: unknown;
  translatedFieldName: string;
  control: FormControl;
}

@Component({
  selector: 'translations-modal',
  templateUrl: './translations-modal.component.html',
  styleUrls: ['./translations-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SkModalContentComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatTooltip,
    TranslatePipe,
    IrisSimpleLoaderModule,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
})
export class TranslationsModalComponent extends SkModalBase<TranslationsModalComponent, any> implements OnInit {
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly translationService = inject(IrisDeeplTranslationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly globalSettingsService = inject(IrisGlobalSettingsService);
  readonly data = inject<TranslationsModalDataI>(MAT_DIALOG_DATA);

  readonly controlTemplate = this.data.controlTemplate;
  readonly originTranslations = this.data.translations ?? {};
  readonly translatedFieldName = this.data.translatedFieldName ?? '';
  readonly currentTranslations = this.data.translations ? this.handleTranslations(this.data.translations) : {};
  enableTranslations: boolean;
  languages: IrisLanguageI[] = [];
  languagesForm: FormGroup;
  readonly originalFieldControl = this.data.control;

  ngOnInit(): void {
    forkJoin([
      this.globalSettingsService.getGlobalSettingsById$(IrisModuleAliasType.Translations).pipe(
        catchError(() => of(null)),
      ),
      this.globalSandbox.languages$.pipe(
        take(1),
        catchError(() => of([])),
      ),
    ]).pipe(
      tap(([languagesSettings, languages]: [
        IrisGlobalSettingsI<{ enableTranslations: boolean; preferredLanguages: string[] }>,
        IrisLanguageI[],
      ]) => {
        const { enableTranslations = false, preferredLanguages } = languagesSettings?.value || {};

        this.enableTranslations = enableTranslations;

        this.languages = languages.filter(lang =>
          !preferredLanguages || this.currentTranslations[lang.name] || preferredLanguages.includes(lang.name),
        );

        const group: Record<string, AbstractControl> = {};
        this.languages.forEach(lang => {
          group[lang.name] = new FormControl(this.currentTranslations[lang.name]);
        });
        this.languagesForm = new FormGroup(group);
        this.cdr.detectChanges();
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  translateOne(targetLanguage: string): void {
    const _targetLanguage = targetLanguage === 'gsw' ? 'de' : targetLanguage === 'sr' ? 'hr' : targetLanguage;

    this.translationService.requestTranslations([{
      targetLanguage: _targetLanguage,
      source: this.originalFieldControl.getRawValue(),
      engine: 'DEEPL',
      format: 'text',
    }]).pipe(
      tap((translatedValues: IrisDeeplResultI[]) => {
        translatedValues.forEach(value => {
          if (value.targetLanguage === 'de') {
            this.languagesForm.patchValue({ gsw: value.translatedText });
          } else if (value.targetLanguage === 'hr') {
            this.languagesForm.patchValue({ sr: value.translatedText });
          }
          this.languagesForm.patchValue({ [value.targetLanguage]: value.translatedText });
        });
        this.cdr.detectChanges();
      }),
      catchError(() => EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  accept(): void {
    const translationsResult = this.languagesForm.value;

    if (this.translatedFieldName) {
      const updatedTranslations = {};
      for (const languageKey of Object.keys(translationsResult)) {
        updatedTranslations[languageKey] = { [this.translatedFieldName]: translationsResult[languageKey] };
      }

      const finalResult = merge(this.originTranslations, updatedTranslations);

      // delete empty properties
      Object.keys(finalResult).forEach((key) => {
        if (this.isNullOrEmpty(finalResult[key][this.translatedFieldName])) {
          if (Object.keys(finalResult[key]).length > 1) {
            delete finalResult[key][this.translatedFieldName];
          }
          else {
            delete finalResult[key];
          }
        }
      });

      this.closeWithResult(finalResult);
    }
    else {
      // delete empty properties
      Object.keys(translationsResult).forEach((key) => {
        if (this.isNullOrEmpty(translationsResult[key])) {
          delete translationsResult[key];
        }
      });
      this.closeWithResult(translationsResult);
    }
  }

  private handleTranslations(translations: unknown): unknown {
    if (this.translatedFieldName) {
      const translationsModalObject = {};
      for (const languageKey of Object.keys(translations)) {
        const translationByLang = translations[languageKey];
        const isFieldPresented = Object.keys(translationByLang).find(field => field === this.translatedFieldName);
        if (isFieldPresented) {
          translationsModalObject[languageKey] = translationByLang[this.translatedFieldName];
        }
      }
      return Object.assign({}, translationsModalObject);
    }
    else {
      return Object.assign({}, translations);
    }
  }

  private isNullOrEmpty(str: string): boolean {
    return !str?.trim();
  }
}
